import { Link } from "gatsby"
import { ReactNode } from "react"
import { FaArrowRight } from "react-icons/fa6"

type FeaturedItem = {
  to: string
  title: string
  description: string
  icon?: ReactNode
  iconBg?: string
  image?: string
  imageAlt?: string
  kicker?: string
}

type SimpleItem = {
  to: string
  title: string
}

type Section = {
  label: string
  items: SimpleItem[]
}

type ViewAll = {
  to: string
  label: string
}

type Props = {
  featured?: {
    label: string
    items: FeaturedItem[]
  }
  sections?: Section[]
  align?: "left" | "right"
  viewAll?: ViewAll
}

function isExternal(to: string) {
  return typeof to === "string" && to.startsWith("http")
}

function MaybeLink({
  to,
  className,
  children,
}: {
  to: string
  className?: string
  children: ReactNode
}) {
  if (isExternal(to)) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {children}
      </a>
    )
  }
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  )
}

const sectionSpanByCount: Record<number, string> = {
  1: "col-span-6",
  2: "col-span-3",
  3: "col-span-2",
}

export function MegaDropdown({
  featured,
  sections = [],
  align = "right",
  viewAll,
}: Props) {
  const alignClass = align === "left" ? "left-0" : "right-0"
  const hasSections = sections.length > 0
  const widthClass = hasSections ? "w-[720px]" : "w-[460px]"
  const featuredSpan = hasSections ? "col-span-6" : "col-span-12"
  const sectionSpan = sectionSpanByCount[sections.length] || "col-span-3"

  return (
    <div
      className={`${widthClass} ${alignClass} absolute top-full pt-3 z-10
      transition opacity-0 group-hover:opacity-100 invisible group-hover:visible
      group-hover:translate-y-0 translate-y-2 duration-300 ease-in-out`}
    >
      <div className="rounded-xl shadow-2xl border border-slate-100 bg-white overflow-hidden">
        <div className="grid grid-cols-12 divide-x divide-slate-100">
          {featured && (
            <div className={`${featuredSpan} p-6`}>
              <p className="text-xs font-semibold tracking-widest uppercase text-zinc-400 mb-4 px-2">
                {featured.label}
              </p>
              <div className="flex flex-col gap-0.5">
                {featured.items.map((item, index) => (
                  <MaybeLink
                    key={index}
                    to={item.to}
                    className="group/item flex items-center gap-4 rounded-lg p-3 transition duration-150 ease-in-out hover:bg-rose-50"
                  >
                    {item.image ? (
                      <span className="flex-none w-11 h-11 rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                        <img
                          src={item.image}
                          alt={item.imageAlt || item.title}
                          className="w-full h-full object-cover"
                        />
                      </span>
                    ) : (
                      <span
                        className="flex-none w-11 h-11 rounded-lg flex items-center justify-center text-white"
                        style={{ backgroundColor: item.iconBg || "#7251F4" }}
                      >
                        {item.icon}
                      </span>
                    )}
                    <span className="flex-1 min-w-0">
                      {item.kicker && (
                        <span className="block text-[10px] font-semibold tracking-widest uppercase text-zinc-400 mb-0.5">
                          {item.kicker}
                        </span>
                      )}
                      <span className="block text-sm font-semibold text-black leading-tight">
                        {item.title}
                      </span>
                      <span className="block text-xs text-zinc-500 mt-1 leading-snug">
                        {item.description}
                      </span>
                    </span>
                  </MaybeLink>
                ))}
              </div>
            </div>
          )}
          {sections.map((section, index) => (
            <div key={index} className={`${sectionSpan} p-6`}>
              <p className="text-xs font-semibold tracking-widest uppercase text-zinc-400 mb-4 px-2">
                {section.label}
              </p>
              <ul className="flex flex-col gap-1">
                {section.items.map((item, i) => (
                  <li key={i}>
                    <MaybeLink
                      to={item.to}
                      className="block text-sm font-medium text-zinc-800 rounded-lg px-2 py-1.5 transition duration-150 ease-in-out hover:text-black hover:bg-rose-50"
                    >
                      {item.title}
                    </MaybeLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {viewAll && (
          <MaybeLink
            to={viewAll.to}
            className="group/viewall flex items-center justify-between border-t border-slate-100 bg-slate-50/60 px-6 py-3 text-sm font-semibold text-black transition duration-150 ease-in-out hover:bg-rose-50"
          >
            <span>{viewAll.label}</span>
            <span className="transition duration-150 ease-in-out group-hover/viewall:translate-x-1">
              <FaArrowRight size={14} />
            </span>
          </MaybeLink>
        )}
      </div>
    </div>
  )
}
